import { apiClient } from "./index";

const normalizeCreditLimit = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const amount = Number(value);
  if (Number.isNaN(amount) || !Number.isFinite(amount)) {
    return null;
  }
  // Match backend DecimalField(max_digits=12, decimal_places=2)
  return Math.round(amount * 100) / 100;
};

// Get customers list with search functionality and pagination
export const getCustomers = async (
  searchTerm = "",
  page = 1,
  pageSize = 25,
  filters = "",
  sortBy = "",
  allData = false,
  purchaseData = null,
  pageFrom = null
) => {
  try {
    const params = new URLSearchParams();
    if (searchTerm) params.append("search", searchTerm);
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("page_size", pageSize.toString());
    if (filters) params.append("filters", filters);
    if (sortBy) params.append("sort_by", sortBy);
    if (allData) params.append("all_data", "True");
    if (purchaseData !== null && purchaseData !== undefined) {
      params.append("purchase_data", String(purchaseData));
    }
    if (pageFrom) {
      params.append("page_from", pageFrom);
    }

    const response = await apiClient.get(
      `/customers/list/?${params.toString()}`
    );
    
    // Return the full paginated response structure
    return { 
      response: {
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
        data: response.data.results || response.data.data || []
      }, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching customers:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch customers",
        data: error.response?.data,
      },
    };
  }
};

// Create new customer
export const createCustomer = async (customerData) => {
  try {
    const payload = {
      email: customerData.email,
      name: customerData.name,
      mobile: customerData.phone, // Map phone to mobile
      gender: "Male", // Static as requested
      address: customerData.address,
      credit_limit: normalizeCreditLimit(customerData.credit_limit),
      purchase_data:
        customerData.purchase_data === 1 ||
        customerData.purchase_data === true ||
        customerData.purchase_data === "1"
          ? 1
          : 0,
    };

    const response = await apiClient.post("/customers/create/", payload);
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error creating customer:", error);
    const errorResponse = error.response?.data;
    return {
      response: null,
      error: {
        message:
          errorResponse?.message ||
          errorResponse?.error ||
          error.message ||
          "Failed to create customer",
        data: errorResponse,
        status: error.response?.status,
        isValidationError: error.response?.status === 400,
        validationErrors:
          errorResponse?.errors || errorResponse?.detail || errorResponse,
        originalError: error,
      },
    };
  }
};

// Update existing customer
export const updateCustomer = async (customerId, customerData) => {
  try {
    const payload = {
      email: customerData.email,
      // password: customerData.password || "password123",
      name: customerData.name,
      mobile: customerData.phone, // Map phone to mobile
      gender: "Male", // Static as requested
      address: customerData.address,
      credit_limit: normalizeCreditLimit(customerData.credit_limit),
      purchase_data:
        customerData.purchase_data === 1 ||
        customerData.purchase_data === true ||
        customerData.purchase_data === "1"
          ? 1
          : 0,
    };

    const response = await apiClient.put(
      `/customers/update/${customerId}/`,
      payload
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error updating customer:", error);
    const errorResponse = error.response?.data;
    return {
      response: null,
      error: {
        message:
          errorResponse?.message ||
          errorResponse?.error ||
          error.message ||
          "Failed to update customer",
        data: errorResponse,
        status: error.response?.status,
        isValidationError: error.response?.status === 400,
        validationErrors:
          errorResponse?.errors || errorResponse?.detail || errorResponse,
        originalError: error,
      },
    };
  }
};

// Get customer detail by ID
export const getCustomerDetail = async (customerId) => {
  try {
    const response = await apiClient.get(`/customers/detail/${customerId}/`);
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching customer detail:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer details",
        data: error.response?.data,
      },
    };
  }
};

// Get customer count statistics
export const getCustomerCount = async (purchaseData = null, pageFrom = null) => {
  try {
    const params = {};
    if (purchaseData !== null && purchaseData !== undefined) {
      params.purchase_data = purchaseData;
    }
    if (pageFrom) {
      params.page_from = pageFrom;
    }
    const config = Object.keys(params).length ? { params } : {};
    const response = await apiClient.get("/customers/count/", config);

    // Handle the new response structure with statistics
    const data = response.data?.data || response.data || {};

    // Normalize the new structure
    const normalizedData = {
      total_customers: {
        count: data.total_customer?.count || 0,
        statistics: data.total_customer?.statistics || {},
      },
      active_customers: {
        count: data.total_active_customer?.count || 0,
        statistics: data.total_active_customer?.statistics || {},
      },
      credit_customers: {
        count: data.credit_to_customer?.count || 0,
        statistics: data.credit_to_customer?.statistics || {},
      },
      outstanding_balance: {
        count: data.outstanding_balance?.count || 0,
        statistics: data.outstanding_balance?.statistics || {},
      },
    };

    return { response: normalizedData, error: null };
  } catch (error) {
    console.error("Error fetching customer count:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer statistics",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get customer details count statistics
export const getCustomerDetailsCount = async (customerId) => {
  try {
    const response = await apiClient.get(
      `/customers/details/count/${customerId}/`
    );
    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching customer details count:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer details statistics",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};

// Get overdue customers with screen_type parameter
export const getOverdueCustomers = async (
  searchTerm = "",
  page = 1,
  pageSize = 25
) => {
  try {
    const params = new URLSearchParams();
    if (searchTerm) params.append("search", searchTerm);
    if (page) params.append("page", page.toString());
    if (pageSize) params.append("page_size", pageSize.toString());
    params.append("screen_type", "overdue");

    const response = await apiClient.get(
      `/customers/list/?${params.toString()}`
    );
    
    // Return the full paginated response structure
    return { 
      response: {
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
        data: response.data.results || response.data.data || []
      }, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching overdue customers:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch overdue customers",
        data: error.response?.data,
      },
    };
  }
};

// Get customer finance journals
export const getCustomerFinanceJournals = async (
  customerId,
  searchQuery = "",
  filters = {},
  allData = false
) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("customer_id", customerId);

    if (searchQuery.trim()) {
      queryParams.append("search", searchQuery.trim());
    }

    // Add date range filters
    if (filters.start_date) {
      queryParams.append("from", filters.start_date);
    }
    if (filters.end_date) {
      queryParams.append("to", filters.end_date);
    }

    // Add transaction type filter
    if (filters.type && filters.type !== "all") {
      queryParams.append("type", filters.type);
    }

    // Add balance filter (positive/negative)
    if (filters.balance && filters.balance !== "all") {
      queryParams.append("balance", filters.balance);
    }

    // Add export-specific zero balance filter
    if (filters.avoid_zero_balance) {
      queryParams.append("avoid_zero_balance", filters.avoid_zero_balance);
    }

    // Add all_data parameter
    if (allData) {
      queryParams.append("all_data", "True");
    }

    const url = `/finance/journals/?${queryParams.toString()}`;
    const response = await apiClient.get(url);

    return { response: response.data, error: null };
  } catch (error) {
    console.error("Error fetching customer finance journals:", error);
    return {
      response: null,
      error: {
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch customer finance journals",
        data: error.response?.data,
        status: error.response?.status,
      },
    };
  }
};
